import {
  PropsWithChildren,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useSnackbar } from "$utils/useSnackbar";
import { downloadFile } from "$components/Export/utils";
import * as JobService from "$services/Job.service";
import {
  JobRecord,
  JobResultKind,
  JobStatus,
  hasDownloadableFile,
  isJobFinished,
} from "$types/Job";
import {
  snackbarJobErrorOptions,
  snackbarJobSuccessOptions,
  snackbarJobWarningOptions,
} from "$utils/SnackbarOptions";
import logger from "$utils/logger";
import { useSocket } from "$utils/socket";
import { getToken } from "$utils/token";

/**
 * Tracks the current user's asynchronous import/export jobs.
 *
 * Updates arrive over Socket.IO. Polling stays on as well: a short export can
 * finish before the client joins its room, and a live socket must not hide that.
 */

const POLL_INTERVAL_MS = 3000;
/** On reload, still report jobs that finished shortly before the page came back. */
const RECENT_COMPLETION_WINDOW_MS = 10 * 60 * 1000;
const NOTIFIED_STORAGE_KEY = "jobs:notified";
const NOTIFIED_HISTORY_SIZE = 100;
const TOAST_DURATION_MS = 8000;

const readNotifiedIds = (): string[] => {
  try {
    const raw = window.localStorage.getItem(NOTIFIED_STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const writeNotifiedIds = (ids: string[]) => {
  try {
    window.localStorage.setItem(
      NOTIFIED_STORAGE_KEY,
      JSON.stringify(ids.slice(-NOTIFIED_HISTORY_SIZE))
    );
  } catch {
    // Storage full or unavailable: notifications simply repeat, nothing breaks.
  }
};

const extensionOf = (fileName: string) => {
  const parts = fileName.split(".");
  return parts.length > 1 ? parts[parts.length - 1] : "xlsx";
};

const linkStyle = {
  background: "none",
  border: "none",
  color: "inherit",
  cursor: "pointer",
  font: "inherit",
  padding: 0,
  textDecoration: "underline",
};

export interface StartJobOptions {
  /** Skip the "traitement lancé" toast when the caller shows its own feedback. */
  silent?: boolean;
}

export interface JobsContextValue {
  /** Active jobs plus those that finished recently. */
  jobs: JobRecord[];
  activeJobs: JobRecord[];
  startJob: (
    input: JobService.CreateJobInput,
    options?: StartJobOptions
  ) => Promise<JobRecord>;
  cancelJob: (jobId: string) => Promise<void>;
  downloadJob: (job: JobRecord) => Promise<void>;
  dismissJob: (jobId: string) => void;
}

const notImplemented = () => {
  throw new Error("useJobs must be used inside a <JobsProvider>");
};

const JobsContext = createContext<JobsContextValue>({
  jobs: [],
  activeJobs: [],
  startJob: notImplemented,
  cancelJob: notImplemented,
  downloadJob: notImplemented,
  dismissJob: notImplemented,
});

export const useJobs = () => useContext(JobsContext);

export const JobsProvider = ({ children }: PropsWithChildren) => {
  const socket = useSocket();
  const [openSuccessSnackbar] = useSnackbar(snackbarJobSuccessOptions);
  const [openWarningSnackbar] = useSnackbar(snackbarJobWarningOptions);
  const [openErrorSnackbar] = useSnackbar(snackbarJobErrorOptions);

  const [jobs, setJobs] = useState<JobRecord[]>([]);
  const notifiedIds = useRef<Set<string>>(new Set(readNotifiedIds()));

  const activeJobs = useMemo(
    () => jobs.filter((job) => !isJobFinished(job)),
    [jobs]
  );

  // Stable key so the socket and polling effects do not resubscribe on every
  // progress tick, only when the set of active jobs actually changes.
  const activeJobIdsKey = useMemo(
    () =>
      activeJobs
        .map((job) => job.id)
        .sort()
        .join(","),
    [activeJobs]
  );

  const upsertJob = useCallback((incoming: JobRecord) => {
    setJobs((previous) => {
      const index = previous.findIndex((job) => job.id === incoming.id);
      if (index === -1) return [incoming, ...previous];
      // A cached poll can still say "pending" after the job has finished.
      if (isJobFinished(previous[index]) && !isJobFinished(incoming)) {
        return previous;
      }
      const next = [...previous];
      next[index] = incoming;
      return next;
    });
  }, []);

  const downloadJob = useCallback(
    async (job: JobRecord) => {
      if (job.result?.kind !== JobResultKind.FILE) return;
      try {
        const blob = await JobService.downloadJobFile(job.id);
        const { fileName } = job.result;
        await downloadFile(blob, fileName, extensionOf(fileName), false);
      } catch (error: any) {
        logger.error(error);
        openErrorSnackbar(
          `${job.label} : ${error?.message ?? "téléchargement impossible."}`,
          TOAST_DURATION_MS
        );
      }
    },
    [openErrorSnackbar]
  );

  const notifyFinishedJob = useCallback(
    (job: JobRecord, autoDownload: boolean) => {
      if (notifiedIds.current.has(job.id)) return;
      notifiedIds.current.add(job.id);
      writeNotifiedIds([...notifiedIds.current]);

      if (job.status === JobStatus.CANCELLED) return;

      if (job.status === JobStatus.FAILED) {
        openErrorSnackbar(
          `${job.label} : ${job.error?.message ?? "échec du traitement."}`,
          TOAST_DURATION_MS
        );
        return;
      }

      const downloadable = hasDownloadableFile(job);
      const degraded = job.status === JobStatus.COMPLETED_WITH_WARNINGS;

      if (downloadable && autoDownload) {
        downloadJob(job);
      }

      let message = `${job.label} : terminé.`;
      if (degraded) {
        message = `${job.label} : terminé avec ${
          job.warnings.length
        } avertissement(s). ${job.warnings[0] ?? ""}`;
      } else if (downloadable) {
        message = `${job.label} : le fichier est prêt${
          autoDownload ? ", téléchargement en cours" : ""
        }.`;
      } else if (
        job.result?.kind === JobResultKind.SUMMARY &&
        job.result.message
      ) {
        message = `${job.label} : ${job.result.message}`;
      }

      const content = (
        <span>
          {`${message} `}
          {downloadable ? (
            <button
              type="button"
              style={linkStyle}
              onClick={() => downloadJob(job)}
            >
              Télécharger
            </button>
          ) : null}
        </span>
      );

      const openSnackbar = degraded ? openWarningSnackbar : openSuccessSnackbar;
      openSnackbar(content, TOAST_DURATION_MS);
    },
    [downloadJob, openErrorSnackbar, openSuccessSnackbar, openWarningSnackbar]
  );

  const handleJobUpdate = useCallback(
    (incoming: JobRecord) => {
      upsertJob(incoming);
      if (isJobFinished(incoming)) notifyFinishedJob(incoming, true);
    },
    [notifyFinishedJob, upsertJob]
  );

  // Effects below must not restart on every render, so they read through refs.
  const handleJobUpdateRef = useRef(handleJobUpdate);
  handleJobUpdateRef.current = handleJobUpdate;
  const notifyFinishedJobRef = useRef(notifyFinishedJob);
  notifyFinishedJobRef.current = notifyFinishedJob;

  // Resume tracking after a reload, and report anything that finished meanwhile.
  useEffect(() => {
    if (!getToken()) return;

    JobService.listJobs()
      .then((records) => {
        const now = Date.now();
        const relevant = records.filter((record) => {
          if (!isJobFinished(record)) return true;
          if (notifiedIds.current.has(record.id)) return false;
          const finishedAt = record.finishedAt
            ? new Date(record.finishedAt).getTime()
            : 0;
          return now - finishedAt < RECENT_COMPLETION_WINDOW_MS;
        });

        setJobs(relevant);
        relevant
          .filter(isJobFinished)
          // No auto-download here: the page just loaded, an unexpected file
          // landing in Downloads would be more confusing than helpful.
          .forEach((record) => notifyFinishedJobRef.current(record, false));
      })
      .catch((error) => logger.error(error));
  }, []);

  useEffect(() => {
    const listener = (record: JobRecord) => handleJobUpdateRef.current(record);
    socket.on("job:updated", listener);
    return () => {
      socket.off("job:updated", listener);
    };
  }, [socket]);

  useEffect(() => {
    if (!activeJobIdsKey) return undefined;
    const ids = activeJobIdsKey.split(",");

    const subscribe = () => socket.emit("jobs:subscribe", ids);
    subscribe();
    // Rooms are lost on reconnect, so join them again.
    socket.on("connect", subscribe);

    return () => {
      socket.off("connect", subscribe);
      socket.emit("jobs:unsubscribe", ids);
    };
  }, [socket, activeJobIdsKey]);

  useEffect(() => {
    if (!activeJobIdsKey) return undefined;
    const ids = activeJobIdsKey.split(",");

    const refresh = () => {
      ids.forEach((id) => {
        JobService.getJob(id)
          .then((record) => handleJobUpdateRef.current(record))
          .catch((error) => logger.error(error));
      });
    };

    refresh();
    const timer = setInterval(refresh, POLL_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [activeJobIdsKey]);

  const startJob = useCallback(
    async (input: JobService.CreateJobInput, options?: StartJobOptions) => {
      const record = await JobService.createJob(input);
      upsertJob(record);
      if (!options?.silent) {
        openSuccessSnackbar(
          `${record.label} : traitement lancé, vous serez notifié à la fin.`,
          5000
        );
      }
      return record;
    },
    [openSuccessSnackbar, upsertJob]
  );

  const cancelJob = useCallback(
    async (jobId: string) => {
      const record = await JobService.cancelJob(jobId);
      upsertJob(record);
    },
    [upsertJob]
  );

  const dismissJob = useCallback((jobId: string) => {
    setJobs((previous) => previous.filter((job) => job.id !== jobId));
  }, []);

  const value = useMemo(
    () => ({
      jobs,
      activeJobs,
      startJob,
      cancelJob,
      downloadJob,
      dismissJob,
    }),
    [jobs, activeJobs, startJob, cancelJob, downloadJob, dismissJob]
  );

  return <JobsContext.Provider value={value}>{children}</JobsContext.Provider>;
};

export default useJobs;
